/** Region a risk-assessment client belongs to. Lower-cased camelCase, matching the backend. */
export type Region = 'uae' | 'usa' | 'europe'

/** A location (venue) the signed-in user is scoped to. */
export interface RiskPortalLocation {
  locationId: string
  name: string
}

/**
 * Profile of the signed-in risk-assessment client, returned by GET /api/risk-assessment/portal/me.
 * Drives which documents the portal asks for (US vs UAE).
 */
export interface RiskPortalProfile {
  firstName: string
  lastName: string
  email: string
  region: Region
  groupId: string
  groupName: string
  locations: RiskPortalLocation[]
}

/** A file the client has uploaded against a requirement slot. */
export interface RiskPortalExternalDocument {
  id: string
  fileName: string
  contentType: string
  fileSizeBytes: number
  uploadedOn: string
  /** Review status as a PascalCase string ("Received", "Accepted", "Rejected", "Moved"). */
  status: string
  /** The reviewer's note when status is "Rejected" — shown so the client knows what to fix; null otherwise. */
  rejectionReason?: string | null
}

/** True when a Hub reviewer rejected this upload — the client should replace it. */
export function isRejectedDocument(document: RiskPortalExternalDocument): boolean {
  return document.status === 'Rejected'
}

/** True when the slot has at least one non-rejected upload (mirrors the backend completion rule). */
export function hasSatisfyingDocument(documents: RiskPortalExternalDocument[]): boolean {
  return documents.some((document) => !isRejectedDocument(document))
}

/** A single checklist slot the portal asks the client to fill, with their uploads against it. */
export interface RiskPortalDocumentRequirement {
  key: string
  label: string
  hint: string | null
  optional: boolean
  documents: RiskPortalExternalDocument[]
}

/**
 * The client's document checklist, returned by GET /api/risk-assessment/portal/documents. The region is
 * resolved server-side and is intentionally not part of this payload. `completedRequired`/`totalRequired`
 * cover only the non-optional slots and drive the progress bar.
 */
export interface RiskPortalDocumentsResponse {
  requirements: RiskPortalDocumentRequirement[]
  completedRequired: number
  totalRequired: number
}

/** Lifecycle of the group's bank connection (camelCase enum names on the wire). */
export type RiskPortalBankConnectionStatus = 'pending' | 'connected' | 'failed' | 'disconnected'

/** Status of one linked bank account (camelCase enum names on the wire). */
export type RiskPortalBankAccountStatus = 'active' | 'inactive' | 'disconnected'

/** A single bank account the client has linked through Stripe Financial Connections. */
export interface RiskPortalBankAccount {
  /** Opaque Stripe FC account handle, used to target the venue-name update. */
  accountId: string
  institutionName: string | null
  displayName: string | null
  last4: string | null
  category: string | null
  status: RiskPortalBankAccountStatus
  currentBalance: number | null
  currency: string | null
  accountHolderNames: string[]
  /**
   * Optional venue name the client types for this account, naming the physical venue it funds. Shown to the
   * Hub assessor so they can map the account to the right Mezza location. Null when the client hasn't set one.
   */
  venueName: string | null
}

/**
 * The client's bank-connection state, returned by GET /api/risk-assessment/portal/banking.
 * `available` is false when Stripe Financial Connections is not enabled for the client's region
 * (e.g. UAE) — the portal then keeps the card informational with a disabled CTA.
 */
export interface RiskPortalBankingResponse {
  available: boolean
  status: RiskPortalBankConnectionStatus | null
  connectedOn: string | null
  accounts: RiskPortalBankAccount[]
}

/**
 * A freshly-created Stripe Financial Connections session, returned by
 * POST /api/risk-assessment/portal/banking/sessions. The client secret is single-use and passed
 * straight to stripe-js's collectFinancialConnectionsAccounts — never persisted or logged.
 */
export interface RiskPortalBankSessionResponse {
  sessionId: string
  clientSecret: string
  publishableKey: string
}
